import {
  forwardRef,
  useMemo,
  useCallback,
  Ref,
  ComponentPropsWithoutRef,
  ChangeEvent,
} from 'react';
import CurrencyInput from 'react-currency-input-field';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faExclamationTriangle,
  faInfoCircle,
  faCheckCircle,
  faExclamationCircle,
} from '@fortawesome/pro-regular-svg-icons';
import { faDollarSign } from '@fortawesome/pro-solid-svg-icons';
import cx from 'classnames';

import { renderAppendedInputNode } from './FormInput';
import style from './FormInput.module.css';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

type FormCurrencyInputProps = {
  status?: 'default' | 'error' | 'warning' | 'success' | 'info';
  suffix?: NonEmptyReactNode | IconProp;
  iconClassName?: string;
  onChange: (e: ChangeEvent | string) => void;
} & ComponentPropsWithoutRef<typeof CurrencyInput>;

const FormCurrencyInput = forwardRef(
  (
    {
      onFocus,
      onChange,
      autoFocus,
      disabled,
      readOnly,
      status = 'default',
      suffix,
      allowDecimals = false,
      decimalsLimit = 2,
      allowNegativeValue = false,
      placeholder,
      iconClassName,
      className,
      ...props
    }: FormCurrencyInputProps,
    ref: Ref<HTMLInputElement>
  ) => {
    const state = useMemo(() => {
      if (disabled) return 'disabled';
      if (readOnly) return 'readonly';
      return status;
    }, [disabled, readOnly, status]);

    const statusIcon = useMemo(() => {
      if (state === 'error') return faExclamationCircle;
      if (state === 'warning') return faExclamationTriangle;
      if (state === 'success') return faCheckCircle;
      if (state === 'info') return faInfoCircle;
      return null;
    }, [state]);

    const handleChange = useCallback(
      (newValue) => {
        if (!newValue) return onChange('');
        return onChange(newValue);
      },
      [onChange]
    );

    return (
      <div className={cx('flex items-center', style.base, style[state])}>
        <div
          className={cx(
            'shrink-0 px-4 border-r border-gray-400 flex justify-center items-center',
            style.statusColor,
            iconClassName
          )}
        >
          <FontAwesomeIcon icon={faDollarSign} size="1x" />
        </div>
        <CurrencyInput
          className={cx(
            'placeholder-gray-500 focus:outline-none w-full py-2 px-4 bg-transparent',
            className
          )}
          disabled={disabled}
          readOnly={readOnly}
          allowDecimals={allowDecimals}
          decimalsLimit={decimalsLimit}
          allowNegativeValue={allowNegativeValue}
          onValueChange={handleChange}
          onFocus={onFocus}
          autoFocus={autoFocus}
          placeholder={placeholder}
          ref={ref}
          {...props}
        />
        {(statusIcon ?? suffix) && (
          <div className={cx('shrink-0 pr-3 transition-colors duration-100', style.statusColor)}>
            {renderAppendedInputNode(statusIcon ?? suffix)}
          </div>
        )}
      </div>
    );
  }
);

FormCurrencyInput.displayName = 'FormCurrencyInput';

export default FormCurrencyInput;
